import { ToastAction } from "@/components/ui/toast";
import { error, success, toast } from "@/hooks/use-toast";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import {
  type RecordShortcutType,
  recordShortcutId,
} from "@/lib/dashboard-home/record-shortcuts";
import {
  DASHBOARD_SHORTCUTS_PREF_KEY,
  MAX_SHORTCUTS,
  parseStoredShortcuts,
  shortcutLabel,
  toggleRecordShortcut,
} from "@/lib/dashboard-home/shortcuts";
import {
  persistBoPreference,
  readBoPreference,
} from "@/lib/user-preferences/store";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

export type PinnableRecord = {
  type: RecordShortcutType;
  id: string;
  title: string;
};

export type RecordShortcutState = { pinned: boolean; toggle: () => void };

/**
 * Épingle une fiche aux raccourcis de l'accueil, ou l'en retire. L'accueil
 * relit la préférence à chaque visite : rien d'autre à invalider.
 * `null` quand la page n'affiche pas une fiche épinglable.
 */
export function useRecordShortcut(
  record: PinnableRecord | null,
): RecordShortcutState | null {
  const vt = useVisibleT();
  const navigate = useNavigate();
  // Sans réglage enregistré, ce sont les raccourcis par défaut : on part
  // d'eux pour ne pas les effacer au premier épinglage.
  const [stored, setStored] = useState(() =>
    parseStoredShortcuts(readBoPreference(DASHBOARD_SHORTCUTS_PREF_KEY)),
  );
  if (!record) return null;
  const { type, id, title } = record;
  const shortcutId = recordShortcutId(type, id);
  const pinned = stored.some((s) => s.id === shortcutId);

  const toggle = () => {
    // Libellé vide pour une fiche sans titre : « Sans titre » est traduit à
    // l'affichage, pas figé dans la préférence.
    const label = shortcutLabel(title);
    const shown = label || vt("bo.home.pin.untitled", "Sans titre");
    const { next, outcome } = toggleRecordShortcut(stored, { type, id, label });

    if (outcome === "full") {
      toast(
        error({
          title: vt("bo.home.pin.fullTitle", "Raccourcis complets"),
          description: vt(
            "bo.home.pin.fullDescription",
            `${MAX_SHORTCUTS} raccourcis maximum : retirez-en un depuis l’accueil.`,
          ),
        }),
      );
      return;
    }

    setStored(next);
    persistBoPreference(DASHBOARD_SHORTCUTS_PREF_KEY, next);
    toast(
      success(
        outcome === "added"
          ? {
              title: vt("bo.home.pin.addedTitle", "Épinglée à l’accueil"),
              description: shown,
              // Laisse le temps de cliquer « Voir ».
              duration: 7000,
              action: (
                <ToastAction
                  altText={vt("bo.home.pin.view", "Voir l’accueil")}
                  onClick={() => navigate("/accueil")}
                >
                  {vt("bo.home.pin.viewShort", "Voir")}
                </ToastAction>
              ),
            }
          : {
              title: vt("bo.home.pin.removedTitle", "Retirée de l’accueil"),
              description: shown,
            },
      ),
    );
  };

  return { pinned, toggle };
}
